import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, ChevronDown, ChevronRight, Dumbbell, Clock,
  CheckCircle, FileText, Calendar, Search, BarChart3, Lock, Share2,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { API } from "../App";
import { useI18n } from "../lib/i18n";

const EXERCISE_LABELS = {
  mood_tracking: { name: "Suivi d'humeur", icon: "heart" },
  emotion_register: { name: "Registre d'émotions", icon: "brain" },
  gratitude: { name: "Journal de gratitude", icon: "star" },
  cognitive_restructuring: { name: "Restructuration cognitive", icon: "refresh" },
  behavioral_activation: { name: "Activation comportementale", icon: "calendar" },
  meditation: { name: "Méditation", icon: "sun" },
  cognitive_defusion: { name: "Défusion cognitive", icon: "wind" },
  self_compassion: { name: "Auto-compassion", icon: "heart" },
  problem_solving: { name: "Résolution de problèmes", icon: "lightbulb" },
  values_exploration: { name: "Exploration des valeurs", icon: "compass" },
  needs_exploration: { name: "Exploration des besoins", icon: "layers" },
  qualities_exploration: { name: "Exploration des qualités", icon: "gem" },
  capabilities_exploration: { name: "Exploration des capacités", icon: "target" },
  assertiveness: { name: "Affirmation de soi", icon: "message" },
  self_reinforcement: { name: "Auto-renforcement", icon: "award" },
  pomodoro: { name: "Pomodoro", icon: "clock" },
  thought_record: { name: "Enregistrement des pensées", icon: "edit" },
  // Barkley
  barkley_identity_card: { name: "Carte d'identité TDAH", icon: "clipboard" },
  barkley_special_moment: { name: "Moment Spécial", icon: "heart" },
  barkley_effective_instructions: { name: "Consignes Efficaces", icon: "message" },
  barkley_token_system: { name: "Système de Points", icon: "award" },
  barkley_crisis_plan: { name: "Plan de Gestion de Crise", icon: "shield" },
  barkley_abc_analysis: { name: "Analyse ABC Barkley", icon: "search" },
  // ACT
  act_dropping_anchor: { name: "Jeter l'Ancre", icon: "anchor" },
  act_expansion: { name: "Faire de la Place", icon: "heart" },
  act_observer_self: { name: "Le Soi Observateur", icon: "eye" },
  act_choice_point: { name: "Le Point de Choix", icon: "git-branch" },
  act_matrix: { name: "La Matrice ACT", icon: "layout" },
  act_committed_action: { name: "Action Engagée", icon: "target" },
  // TDAH Adulte
  tdah_priorities_abc: { name: "Priorités ABC", icon: "list" },
  tdah_document_management: { name: "Gestion des Documents", icon: "folder" },
  tdah_attention_capacity: { name: "Capacité Attentionnelle", icon: "focus" },
  tdah_automatic_thoughts: { name: "Pensées Automatiques TDAH", icon: "brain" },
  tdah_anti_procrastination: { name: "Anti-Procrastination", icon: "timer" },
  tdah_goals_motivation: { name: "Objectifs et Motivation", icon: "flag" },
  // TDAH Kids
  tdah_kids_relaxation: { name: "Relaxation TDAH", icon: "sun" },
  tdah_kids_therapist_questions: { name: "Questions Thérapeute", icon: "message" },
  tdah_kids_superhero_day: { name: "Journée Super-Héros", icon: "zap" },
  tdah_kids_agitation_radar: { name: "Radar d'Agitation", icon: "radio" },
  tdah_kids_turbo_brain: { name: "Cerveau Turbo", icon: "brain" },
  tdah_kids_victories: { name: "Victoires", icon: "trophy" },
  tdah_kids_superpowers: { name: "Super-Pouvoirs", icon: "star" },
  tdah_kids_recharge: { name: "Recharge", icon: "battery" },
  tdah_kids_mission: { name: "Mission", icon: "target" },
  tdah_kids_safety_bubble: { name: "Bulle de Sécurité", icon: "shield" },
  // Other
  dissociation: { name: "Exercices Dissociation", icon: "layers" },
  schema_therapy: { name: "Thérapie des Schémas", icon: "grid" },
  tcd: { name: "Thérapie Comportementale Dialectique", icon: "scale" },
  exercise_entries: { name: "Autres Exercices", icon: "file-text" },
};

const SKIP_FIELDS = ["user_id", "entry_id", "record_id", "session_id", "created_at", "updated_at", "exercise_type", "data", "shared_with_therapist", "not_shared", "_id", "assignment_id"];

const ENUM_LABELS = {
  nourished: { yes: "Oui", no: "Non", partial: "Un peu" },
  priority: { high: "Haute", medium: "Moyenne", low: "Basse" },
};

const formatValue = (key, val) => {
  if (val === null || val === undefined || val === "") return "—";
  if (typeof val === "boolean") return val ? "Oui" : "Non";
  if (typeof val === "number") {
    const isRating = key && (key.includes("intensity") || key.includes("level") || key.includes("rating") || key === "mood_level" || key === "calm_level" || key === "satisfaction" || key === "importance");
    return isRating ? `${val}/10` : String(val);
  }
  if (typeof val === "string") {
    if (ENUM_LABELS[key] && ENUM_LABELS[key][val]) return ENUM_LABELS[key][val];
    if (val.match(/^\d{4}-\d{2}-\d{2}/)) {
      try { return new Date(val).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
      catch { return val; }
    }
    return val;
  }
  if (Array.isArray(val)) {
    if (val.length === 0) return "—";
    if (typeof val[0] === "string" || typeof val[0] === "number") return val.join(", ");
    if (typeof val[0] === "object") {
      return (
        <div className="space-y-1 mt-1">
          {val.slice(0, 15).map((item, i) => (
            <div key={i} className="ml-2 pl-2 border-l-2 border-muted/50 py-0.5">
              {Object.entries(item).filter(([k]) => !SKIP_FIELDS.includes(k) && item[k] != null && item[k] !== "").map(([k, v]) => {
                const label = FIELD_LABELS[k] || k.replace(/_/g, " ").replace(/([A-Z])/g, " $1").trim();
                return (
                  <div key={k} className="text-xs">
                    <span className="font-medium">{label}:</span>{" "}
                    <span>{formatValue(k, v)}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      );
    }
    return "—";
  }
  if (typeof val === "object") {
    return (
      <div className="ml-2 mt-0.5 space-y-0.5">
        {Object.entries(val).filter(([k]) => !SKIP_FIELDS.includes(k) && val[k] != null && val[k] !== "").map(([k, v]) => {
          const label = FIELD_LABELS[k] || k.replace(/_/g, " ").replace(/([A-Z])/g, " $1").trim();
          return (
            <div key={k} className="text-xs">
              <span className="font-medium">{label}:</span>{" "}
              <span>{formatValue(k, v)}</span>
            </div>
          );
        })}
      </div>
    );
  }
  return "—";
};

const FIELD_LABELS = {
  situation: "Situation", automatic_thoughts: "Pensees automatiques", automatic_thought: "Pensee automatique",
  emotions: "Emotions", emotion_intensity: "Intensite", emotion_intensity_before: "Intensite avant",
  emotion_intensity_after: "Intensite apres", new_emotion_intensity: "Nouvelle intensite",
  balanced_thought: "Pensee equilibree", alternative_thought: "Pensee alternative",
  socratic_q1: "Question socratique 1", socratic_q2: "Question socratique 2", socratic_q3: "Question socratique 3",
  externalization_letter: "Lettre d'externalisation", evidence_for: "Arguments pour", evidence_against: "Arguments contre",
  distortions: "Distorsions cognitives", action_plan: "Plan d'action",
  currentStyle: "Style actuel", descD: "Decrire (D)", descE: "Exprimer (E)", descS: "Specifier (S)",
  descC: "Consequences (C)", practiceResponse: "Reponse assertive", reflection: "Reflexion",
  mood_level: "Niveau d'humeur", notes: "Notes", items: "Elements",
  accomplishment: "Accomplissement", difficulty_level: "Difficulte", emotions_felt: "Emotions ressenties",
  reward_chosen: "Recompense choisie", problem: "Probleme", goals: "Objectifs",
  solutions: "Solutions", selectedSolution: "Solution choisie", actionPlan: "Plan d'action",
  qualities: "Qualites", capabilities: "Competences",
  domains: "Domaines", needs: "Besoins", importance: "Importance", alignment: "Alignement",
  satisfaction: "Satisfaction", description: "Description", actions: "Actions", rating: "Note",
  activities: "Activites", date: "Date", duration: "Duree", duration_minutes: "Duree (min)",
  meditation_type: "Type de meditation", technique: "Technique", calm_level: "Niveau de calme",
  thought: "Pensee", fusion_before: "Fusion avant", fusion_after: "Fusion apres",
  comfort_level: "Niveau de confort", self_criticism: "Auto-critique",
  compassionate_response: "Reponse bienveillante", sleep_quality: "Qualite du sommeil",
  intention: "Intention", checked: "Coche", task_name: "Tache", completed: "Termine",
  focus_rating: "Concentration", task: "Tache", pleasure_rating: "Plaisir", mastery_rating: "Maitrise",
  hooks: "Hamecons", away_moves: "Mouvements d'eloignement", toward_moves: "Mouvements d'approche",
  value: "Valeur", goal: "Objectif", smallest_step: "Plus petit pas", obstacle: "Obstacle",
  trigger: "Declencheur", acknowledge: "Reconnaitre", connect: "Se connecter", engage: "S'engager",
  emotion: "Emotion", body_location: "Localisation corporelle", observe: "Observer",
  breathe_into: "Respirer dans", make_room: "Faire de la place",
  important: "Important", inner_obstacles: "Obstacles internes",
  away_behaviors: "Comportements d'eloignement", toward_behaviors: "Comportements d'approche",
  current_thoughts: "Pensees actuelles", current_emotions: "Emotions actuelles",
  notice_observing: "Observer", still_here: "Toujours la",
  behavior: "Comportement", functions: "Fonctions", feeling: "Ressenti", response: "Reponse",
  location: "Lieu", sensations: "Sensations", intensity: "Intensite", boredom: "Ennui",
  urge: "Impulsivite", action_taken: "Action prise", feeling_after: "Ressenti apres",
  feeling_during: "Ressenti pendant", activity: "Activite",
  scenario: "Scenario", usual_behavior: "Comportement habituel", turbo_says: "Turbo dit",
  outcome: "Resultat", mission_activity: "Activite mission", result: "Resultat",
  steps_completed: "Etapes completees", color: "Couleur", shape: "Forme", texture: "Texture",
  noise_level: "Niveau de bruit", signal: "Signal", periods: "Periodes", energy: "Energie",
  need: "Besoin", name: "Nom", superpowers_known: "Super-pouvoirs connus",
  superpowers_custom: "Super-pouvoirs personnels", questions: "Questions",
  turbo_helps: "Turbo aide", brake_tired: "Frein fatigue",
  victory: "Victoire", proud_because: "Fier parce que",
  task_a1: "Tache A", task_b1: "Tache B", task_c1: "Tache C", first_action: "Premiere action",
  entries: "Entrees", responses: "Reponses", completed_phrases: "Phrases completees",
  text: "Texte", pros: "Pour", cons: "Contre", step: "Etape",
  quality: "Qualite", example: "Exemple", source: "Source",
  capability: "Competence", evidence: "Preuve", level: "Niveau",
  effectiveness: "Efficacite", awareness: "Prise de conscience",
  after_feeling: "Ressenti apres", values_at_stake: "Valeurs en jeu",
  chosen_action: "Action choisie", category: "Categorie",
  timeSlot: "Creneau horaire", see5: "5 choses vues",
  hear4: "4 choses entendues", touch3: "3 choses touchees",
  smell2: "2 choses senties", taste1: "1 chose goutee",
  // Frontend form field names (older data may use these)
  current_style: "Style actuel", describe: "Decrire (D)", express: "Exprimer (E)",
  specify: "Specifier (S)", consequences: "Consequences (C)",
  reformulated_response: "Reponse assertive",
  chosen_solution: "Solution choisie", action_steps: "Plan d'action",
  pros_cons: "Pour et Contre",
  needs_table: "Besoins specifiques",
  nourished: "Nourri", priority: "Priorite", deadline: "Echeance",
  physiological: "Physiologique", safety: "Securite", belonging: "Appartenance",
  esteem: "Estime", selfActualization: "Accomplissement",
};

const EntryCard = ({ entry, index }) => {
  const [expanded, setExpanded] = useState(false);

  // Handle non-shared entries
  if (entry.not_shared) {
    const dateStr = entry.created_at;
    const dateFormatted = dateStr ? (() => {
      try { return new Date(dateStr).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
      catch { return dateStr; }
    })() : null;

    return (
      <div className="border rounded-lg p-3 bg-muted/20 border-dashed" data-testid={`entry-${index}-not-shared`}>
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {dateFormatted || `Entree ${index + 1}`}
          </span>
          <span className="text-xs text-muted-foreground flex items-center gap-1.5 bg-muted px-2 py-0.5 rounded-full">
            <Lock className="w-3 h-3" />
            Non partage par le patient
          </span>
        </div>
      </div>
    );
  }

  // Unwrap nested data field for exercise_entries
  let actualEntry = entry;
  if (entry.data && typeof entry.data === "object") {
    actualEntry = { ...entry, ...entry.data };
  }

  const displayFields = Object.entries(actualEntry).filter(([k]) => !SKIP_FIELDS.includes(k) && k !== "data");
  const previewFields = displayFields.slice(0, 3);
  const hasMore = displayFields.length > 3;

  const dateStr = entry.created_at || entry.updated_at || entry.date;
  const dateFormatted = dateStr ? (() => {
    try { return new Date(dateStr).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return dateStr; }
  })() : null;

  return (
    <div className="border rounded-lg p-3 hover:bg-muted/30 transition-colors" data-testid={`entry-${index}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {dateFormatted || `Entrée ${index + 1}`}
          {entry.shared_with_therapist && (
            <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 rounded-full">partage</span>
          )}
        </span>
        {hasMore && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-primary flex items-center gap-0.5 hover:underline"
          >
            {expanded ? "Réduire" : `+${displayFields.length - 3} champs`}
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        )}
      </div>
      <div className="space-y-1.5">
        {(expanded ? displayFields : previewFields).map(([key, val]) => {
          const label = FIELD_LABELS[key] || key.replace(/_/g, " ").replace(/([A-Z])/g, " $1").trim();
          const rendered = formatValue(key, val);
          const isComplex = typeof rendered === "object" && rendered !== null;
          return (
            <div key={key} className="text-sm">
              <span className="font-medium text-foreground/70">{label}: </span>
              {isComplex ? rendered : <span className="text-foreground">{rendered}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ExerciseSection = ({ typeId, entries }) => {
  const [open, setOpen] = useState(false);
  const label = EXERCISE_LABELS[typeId] || { name: typeId };
  const entryCount = entries.length;

  // For standalone exercises (Barkley/ACT/TDAH), flatten the data field
  const processedEntries = entries.map(e => {
    if (e.data && typeof e.data === "object") {
      return { ...e.data, created_at: e.created_at || e.updated_at };
    }
    return e;
  });

  return (
    <Card className={`transition-all ${open ? "ring-1 ring-primary/20" : ""}`} data-testid={`exercise-section-${typeId}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors rounded-t-lg"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <Dumbbell className="w-4 h-4 text-primary" />
          </div>
          <div>
            <p className="font-medium text-sm">{label.name}</p>
            <p className="text-xs text-muted-foreground">{entryCount} entrée{entryCount > 1 ? "s" : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
            {entryCount}
          </span>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </button>
      {open && (
        <CardContent className="pt-0 pb-4 space-y-2 max-h-[500px] overflow-y-auto">
          {processedEntries.map((entry, i) => (
            <EntryCard key={i} entry={entry} index={i} />
          ))}
        </CardContent>
      )}
    </Card>
  );
};

const PatientExerciseReview = ({ user, logout }) => {
  const { t } = useI18n();
  const { patientId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/therapist/patient-exercises/${patientId}`, { withCredentials: true });
        setData(res.data);
      } catch (err) {
        console.error("Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [patientId]);

  const filteredExercises = useMemo(() => {
    if (!data?.standalone) return {};
    if (!searchTerm) return data.standalone;
    const lower = searchTerm.toLowerCase();
    const filtered = {};
    Object.entries(data.standalone).forEach(([type, entries]) => {
      const label = EXERCISE_LABELS[type]?.name || type;
      if (label.toLowerCase().includes(lower) || type.toLowerCase().includes(lower)) {
        filtered[type] = entries;
      }
    });
    return filtered;
  }, [data, searchTerm]);

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse h-16 bg-muted rounded-lg" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="text-center py-12">
          <p className="text-muted-foreground">{t("common.error") || "Erreur de chargement"}</p>
          <Link to="/therapist/patients">
            <Button variant="link" className="mt-2"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const { patient, summary, assignments } = data;
  const exerciseTypes = Object.keys(filteredExercises);

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="patient-exercise-review">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to={`/therapist/patients/${patientId}`}>
              <Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button>
            </Link>
            <div>
              <h1 className="font-heading text-2xl font-semibold">{patient.name}</h1>
              <p className="text-sm text-muted-foreground">{t("therapist.exerciseReview") || "Revue des exercices"}</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <BarChart3 className="w-5 h-5 mx-auto text-primary mb-1" />
              <p className="text-2xl font-semibold">{summary.total_entries}</p>
              <p className="text-xs text-muted-foreground">{t("therapist.totalEntries") || "Total entrées"}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Dumbbell className="w-5 h-5 mx-auto text-blue-500 mb-1" />
              <p className="text-2xl font-semibold">{summary.exercise_types_active}</p>
              <p className="text-xs text-muted-foreground">{t("therapist.activeTypes") || "Types actifs"}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-5 h-5 mx-auto text-emerald-500 mb-1" />
              <p className="text-2xl font-semibold">{summary.completed_assignments}</p>
              <p className="text-xs text-muted-foreground">{t("therapist.completed") || "Complétés"}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="w-5 h-5 mx-auto text-amber-500 mb-1" />
              <p className="text-2xl font-semibold">{summary.pending_assignments}</p>
              <p className="text-xs text-muted-foreground">{t("therapist.pending") || "En cours"}</p>
            </CardContent>
          </Card>
          {summary.total_exercise_entries > 0 && (
            <Card>
              <CardContent className="p-4 text-center">
                <Share2 className="w-5 h-5 mx-auto text-violet-500 mb-1" />
                <p className="text-2xl font-semibold">{summary.shared_entries}/{summary.total_exercise_entries}</p>
                <p className="text-xs text-muted-foreground">Partages</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t("therapist.searchExerciseType") || "Rechercher un type d'exercice..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="search-exercise-types"
          />
        </div>

        {/* Exercise Sections */}
        {exerciseTypes.length > 0 ? (
          <div className="space-y-3">
            {exerciseTypes.map(type => (
              <ExerciseSection
                key={type}
                typeId={type}
                entries={filteredExercises[type]}
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">
                {searchTerm
                  ? (t("therapist.noExercisesFound") || "Aucun exercice trouvé")
                  : (t("therapist.noExerciseData") || "Ce patient n'a pas encore d'exercices")
                }
              </p>
            </CardContent>
          </Card>
        )}

        {/* Assignments */}
        {assignments.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {t("therapist.allAssignments") || "Tous les exercices assignés"}
                <span className="text-xs bg-muted px-2 py-0.5 rounded-full">{assignments.length}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y max-h-80 overflow-y-auto">
                {assignments.map(a => (
                  <div key={a.assignment_id} className="py-2.5 flex items-center justify-between text-sm">
                    <div>
                      <span className="font-medium">{a.title}</span>
                      {a.instructions && <span className="text-muted-foreground ml-2 text-xs">— {a.instructions}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {a.created_at ? new Date(a.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : ""}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        a.status === "completed" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" :
                        "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                      }`}>
                        {a.status === "completed" ? "Complété" : "En cours"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PatientExerciseReview;
